import { useEffect, useState, useMemo } from "react";
import { useWallet } from "../hooks/useWallet";
import { useAccess } from "../hooks/useAccess";
import { useInstanceStore } from "../stores/UseInstanceStore";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";

const FEATURE_LABELS = {
  TOKEN_COUNT: "Tokens",
  RECOMMENDATION: "Recommendations",
  PAPER_TRADE: "Paper trades",
  LIVE_TRADE: "Live trades",
};

/**
 * AppAlerts — compact header alert bar.
 * Shows only when:
 *   - subscription expires_soon / expires_very_soon / is_expired, OR
 *   - any feature_usage item is >= 80% of its limit
 * Silently renders nothing otherwise.
 */
export default function AppAlerts() {
  const { jwtToken } = useWallet();
  const { checkout: checkoutOpen } = useAccess();
  const apiBaseUrl = useInstanceStore((s) => s.instance.apiBaseUrl);

  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    if (!jwtToken || !apiBaseUrl) return;

    const fetchUser = async () => {
      try {
        const res = await fetch(`${apiBaseUrl.replace(/\/+$/, "")}/user`, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        });
        if (!res.ok) return;
        setData(await res.json());
      } catch {
        // fail silently — non-critical indicator
      }
    };

    fetchUser();
  }, [jwtToken, apiBaseUrl]);

  const alerts = useMemo(() => {
    if (!data) return [];

    const sub = data?.subscription;
    const result = [];

    // --- Subscription expiry alerts ---
    // Suppressed once checkout is closed (KAT-230): every one of these ends in
    // "upgrade" or "renew", and there is no longer a way to do either.
    if (checkoutOpen && sub && !sub.is_forever) {
      if (sub.is_expired) {
        result.push({
          id: "sub-expired",
          level: "error",
          message: "Your subscription has expired. Upgrade to restore access.",
        });
      } else if (sub.expires_very_soon) {
        const hrs = sub.hours_remaining;
        result.push({
          id: "sub-critical",
          level: "error",
          message: `Subscription expires in ${hrs != null ? `${hrs}h` : "< 24h"}. Renew now.`,
        });
      } else if (sub.expires_soon) {
        const days = sub.days_remaining;
        result.push({
          id: "sub-warning",
          level: "warning",
          message: `Subscription expires in ${days != null ? `${days} day${days !== 1 ? "s" : ""}` : "a few days"}.`,
        });
      }
    }

    // --- Feature usage >= 80% alerts ---
    const featureUsage = data?.feature_usage ?? [];
    for (const f of featureUsage) {
      if (!f.limit_count || f.limit_count <= 0) continue;
      const pct = Math.round((f.usage_count / f.limit_count) * 100);
      if (pct < 80) continue;
      const label = FEATURE_LABELS[f.feature_type] ?? f.feature_type;
      result.push({
        id: `usage-${f.feature_type}`,
        level: pct >= 95 ? "error" : "warning",
        message: `${label} at ${pct}% (${f.usage_count} / ${f.limit_count}).`,
      });
    }

    return result.filter((a) => !dismissed.includes(a.id));
  }, [data, dismissed, checkoutOpen]);

  if (alerts.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 mt-1">
      {alerts.map((alert) => (
        <AlertBanner
          key={alert.id}
          alert={alert}
          onDismiss={() => setDismissed((prev) => [...prev, alert.id])}
        />
      ))}
    </div>
  );
}

function AlertBanner({ alert, onDismiss }) {
  const isError = alert.level === "error";

  return (
    <div
      className={[
        "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium",
        isError
          ? "bg-red-900/50 border border-red-500/40 text-red-300"
          : "bg-yellow-900/50 border border-yellow-500/40 text-yellow-300",
      ].join(" ")}
    >
      <FaExclamationTriangle
        size={11}
        className={isError ? "text-red-400 shrink-0" : "text-yellow-400 shrink-0"}
      />
      <span className="flex-1 leading-tight">{alert.message}</span>
      <button
        onClick={onDismiss}
        className="ml-1 shrink-0 opacity-60 hover:opacity-100 focus:outline-none"
        aria-label="Dismiss"
      >
        <FaTimes size={10} />
      </button>
    </div>
  );
}
